/**
 * The contract between the Character Editor app screen and the
 * `sb_character_editor.riv` file.
 *
 * Verified against the file (fixes06) on 2026-07-03 with `.temp/rive-inspect.mjs`:
 *   • 1 artboard "Editor" (700×700, head only)
 *   • 12 timeline animations (each 1.000s @ 60fps): the two head turns, four
 *     eye-close lids ("L/R Eye Close", "L/R Eye B Close"), mouth pose
 *     ("Mouth Horizontally/Vertically", "SmileControl"), eyebrows
 *     ("Eyebrows Turn/Vertically") and "Test"
 *   • 1 state machine "Story animation" (0 inputs)
 *   • view model "ViewModel1": 9 color props + `isGirl` / `frecklesOn` /
 *     `glassesOn` booleans
 *
 * Colour/boolean/enum editing only lights up once these properties are added to
 * the view model in the Rive editor and bound to the corresponding shapes. The
 * screen is data-driven: it discovers whatever the file exposes at runtime and
 * uses the maps below only to label/group known names. Unknown properties still
 * render under "Other", so nothing is lost if a name differs.
 */

export const RIV_SRC = '/rive/sb_character_editor/sb_character_editor.riv';

/**
 * Hand-editable skin-set overrides. When this file exists it is loaded over
 * the committed `CHARACTER_SKIN_PALETTE` on editor load (per-index, per-key
 * merge — missing keys keep the built-in value). Accepts either a bare array
 * or a full exported character-palette.json (`{ skinPalette: [{index, set}] }`),
 * so an Export can be pasted straight in. Edit hexes → reload the page.
 * `baseHex` in the file is informational only — the app tone stays fixed.
 */
export const SKIN_SETS_URL = '/rive/sb_character_editor/skin-sets.json';

/** Dev-server endpoint (vite.config.ts `saveSkinSetsPlugin`) that writes the
 *  file above. The editor's Update / Save all buttons POST the current sets
 *  here, so in-editor edits land in the same hand-editable file. */
export const SKIN_SETS_SAVE_URL = '/__save-skin-sets';

/** `_readme` written into skin-sets.json on every save. */
export const SKIN_SETS_README =
  "Hand-editable skin sets for the Character Editor (/debug/character-editor). Edit any hex below and RELOAD the page — the editor loads this file over the built-in palette. The editor's Update / Save all buttons also WRITE this file, so it always holds the latest sets. 'baseHex' is the app's fixed onboarding tone (identity only, changing it here has no effect); the colours inside 'set' are what the character actually uses. Delete this file to fall back to the palette committed in code.";

/**
 * Hand-editable hair-colour sets — the layered-hair analogue of skin-sets.json.
 * Loaded over the seeded defaults on editor load (per-index merge) and WRITTEN
 * by the Update / Save all buttons, so the editor always holds the latest
 * shades. Each entry is `{ index, name, shades: [dark→light], eyebrow, beard }`.
 *
 * `beard` is a SECOND ramp of the same length, for Dad's facial hair: it hangs off
 * the hair colour (pick a hair colour → the beard follows it) but is authored
 * separately so a beard can be greyer or darker than the head hair. Missing ⇒ that
 * colour's beard uses its `shades`.
 */
export const HAIR_SETS_URL = '/rive/sb_character_editor/hair-sets.json';

/** Dev-server endpoint (vite.config.ts `saveHairSetsPlugin`) that writes the
 *  file above. */
export const HAIR_SETS_SAVE_URL = '/__save-hair-sets';

/** `_readme` written into hair-sets.json on every save. */
export const HAIR_SETS_README =
  "Hand-editable hair colour sets for the Character Editor (/debug/character-editor). Each of the 8 hair colours holds a per-layer shade ramp ('shades', bottom→top / dark→light) that is recoloured onto the grayscale layered hair PNGs in code, an 'eyebrow' colour, and a 'beard' ramp of the same shape used for Dad's facial hair (omit it and the beard uses 'shades'). Edit any hex and RELOAD the page — the editor loads this file over the seeded defaults. The Update / Save all buttons also WRITE this file. Delete it to fall back to the seeded ramp of each colour's preset hex.";

/**
 * Per-hairstyle tags authored in the editor (HAIRSTYLES section). These are
 * asset METADATA (not per-character), keyed by style name and split front/back:
 * `{ front: { "<style>": ["curly","long"] }, back: { … } }`. The photo-analysis
 * tool feeds them into the model's prompt so it matches a photo to the
 * best-tagged style. Written by the tag editor, loaded on mount.
 */
export const HAIR_TAGS_URL = '/rive/sb_character_editor/hair-tags.json';

/** Dev-server endpoint (vite.config.ts `saveHairTagsPlugin`) that writes it. */
export const HAIR_TAGS_SAVE_URL = '/__save-hair-tags';

/** `_readme` written into hair-tags.json on every save. */
export const HAIR_TAGS_README =
  "Per-hairstyle tags for the Character Editor (/debug/character-editor). Keyed by style name under 'front' and 'back'. Authored via the tag editor in the HAIRSTYLES section; the photo → character tool sends these tags to the vision model so it matches a photo to the best-tagged hairstyle. Delete this file to clear all tags.";

/**
 * Per-character DEFAULT look — which skin tone, eye colour, hair colour,
 * hairstyles and toggles a character opens on.
 *
 * The palette files above hold the COLOUR VALUES; this one holds the SELECTIONS,
 * which nothing else persists (they lived only in the in-memory look store, so a
 * reload threw them away). Written by the "Use as default" button, per character, and
 * read on mount by both the Character and Animation tabs (Tamara, 2026-08-04: "so
 * when I set skin, hair and color it stays the same on reload").
 *
 * Bool/enum keys are CANONICAL (`isGirl`, not `dIsGirl`) so one entry means the same
 * thing to every character — the same convention `characterLookStore` uses.
 */
export const CHARACTER_DEFAULTS_URL = '/rive/sb_character_editor/character-defaults.json';

/** Dev-server endpoint (vite.config.ts `saveCharacterDefaultsPlugin`) that writes
 *  the file above. It merges ONE character per POST, so saving Dad's default can't
 *  overwrite Mom's. */
export const CHARACTER_DEFAULTS_SAVE_URL = '/__save-character-defaults';

/** `_readme` written into character-defaults.json on every save. */
export const CHARACTER_DEFAULTS_README =
  "Per-character default look for the Character Editor (/debug/character-editor) — which skin tone, eye colour, hair colour, hairstyles, beard and toggles each character opens on. Written by the 'Use as default' button (one character per save) and read by both the Character and Animation tabs on load. The COLOURS themselves live in skin-sets.json / hair-sets.json; this file only records which of them is selected. Bool/enum keys are canonical (isGirl, frecklesOn) and apply to every character. Delete an entry to reopen that character on the built-in defaults.";

/** Fallback artboard (the full scene) used until a head-only artboard exists. */
export const ARTBOARD = 'Scene test';

/**
 * The editor prefers a dedicated head-only artboard so the preview shows just
 * the head, big & centred (Fit.Contain auto-fits it) — no body, no colour
 * reference. Name the new artboard one of these in Rive (first match wins); it
 * must carry the two head-turn animations. Until it exists, the screen falls
 * back to {@link ARTBOARD}.
 */
export const HEAD_ARTBOARD_CANDIDATES = ['Editor', 'Head', 'Character', 'Head turn'];

/** Pick the preferred artboard from the file's artboard names (or undefined). */
export function pickHeadArtboard(names: string[]): string | undefined {
  const lower = names.map((n) => n.toLowerCase());
  for (const cand of HEAD_ARTBOARD_CANDIDATES) {
    const i = lower.indexOf(cand.toLowerCase());
    if (i !== -1) return names[i];
  }
  return undefined;
}

/** Reference canvas (Figma/Rive top-left origin). See `figma-precision`. */
export const REF_W = 2400;
export const REF_H = 1400;

/** Head-turn timeline animations + their length (seconds). Neutral = duration/2. */
export const HEAD_TURN_H = 'Turn head Horizontally';
export const HEAD_TURN_V = 'Turn head Vertically';
export const HEAD_TURN_DURATION_S = 1.0;

/**
 * Face-pose timelines (fixes06) — every pose timeline in the file is 1.000s.
 * Eye close comes as four per-lid timelines; the editor drives L+R together
 * ("Both Eyes Close" / "Both Eyes Bottom Close" comb controllers): 0 = open,
 * 1 = fully closed.
 *
 * ⚠ JOYSTICK RIG (fixes06): all of these timelines are OWNED BY JOYSTICKS in
 * the .riv (verified by binary inspection — 8 Joystick objects map x/yId to
 * these animations). A joystick re-applies its timeline at its own position on
 * EVERY artboard advance, which overwrites a direct runtime scrub — so
 * scrubbing them (the old-file approach, kept below as a fallback) has no
 * visible effect on this rig, and the runtime has no joystick API.
 *
 * SOLUTION (zero designer work): `scripts/rive/rive-add-controls.py` post-processes
 * each designer export and injects the {@link CTRL_TIMELINES} — tiny 1s
 * timelines that key the JOYSTICK POSITIONS themselves (-1 → 1), which IS
 * runtime-applicable (the designer's "Test" timeline does the same thing).
 * The screen scrubs those exactly like the pre-joystick head-turn timelines.
 * Priority per control: CTRL timeline → bound ViewModel number
 * ({@link POSE_NUMBER_PROPS}, optional) → raw timeline scrub (pre-joystick
 * files only).
 */
export const EYES_TOP_CLOSE = ['L Eye Close', 'R Eye Close'] as const;
export const EYES_BOTTOM_CLOSE = ['L Eye B Close', 'R Eye B Close'] as const;
/** Mouth-shape joystick axes + the smile slider (neutral = midpoint 0.5). */
export const MOUTH_H = 'Mouth Horizontally';
export const MOUTH_V = 'Mouth Vertically';
export const MOUTH_SMILE = 'SmileControl';
export const POSE_DURATION_S = 1.0;

/**
 * Control timelines injected by `scripts/rive/rive-add-controls.py`. To import a
 * new designer export, run the wrapper (patches + prunes old exports in one go):
 * `scripts/rive/import-character-editor.sh` (auto-picks the highest fixesNN) or
 * `scripts/rive/import-character-editor.sh 12` for a specific one. Each keys the
 * matching joystick's x/y from -1 (t=0) to +1 (t=1s); `mouthOpen` keys the
 * `_M-Open`/`_M-Closed` Solo with hold keyframes (t=0 → closed, t≥0.5 → open).
 * The screen scrubs whichever of these exist in the loaded file.
 */
export const CTRL_TIMELINES = {
  headX: 'CTRL headX',
  headY: 'CTRL headY',
  mouthX: 'CTRL mouthX',
  mouthY: 'CTRL mouthY',
  smile: 'CTRL smile',
  eyesTopClose: 'CTRL eyesTopClose',
  eyesBottomClose: 'CTRL eyesBottomClose',
  mouthOpen: 'CTRL mouthOpen',
} as const;

/**
 * ViewModel NUMBER property names for the joystick-driven pose rig. Each must
 * be created on ViewModel1 (type: number) and bound to the matching Joystick
 * axis in Rive. Values are written in joystick space: -1 … 1, centre 0 (the
 * editor UI maps its 0…1 sliders linearly). One property can bind to BOTH the
 * L and R lid joysticks — that is the "both eyes" combining.
 */
export const POSE_NUMBER_PROPS = {
  headX: 'headX', //           → Head joystick X  ("Turn head Horizontally")
  headY: 'headY', //           → Head joystick Y  ("Turn head Vertically")
  eyesTopClose: 'eyesTopClose', //     → L+R top-lid joystick Y  ("L/R Eye Close")
  eyesBottomClose: 'eyesBottomClose', // → L+R bottom-lid joystick Y ("L/R Eye B Close")
  mouthX: 'mouthX', //         → Mouth joystick X ("Mouth Horizontally")
  mouthY: 'mouthY', //         → Mouth joystick Y ("Mouth Vertically")
  smile: 'smile', //           → Smile joystick Y ("SmileControl")
} as const;

/**
 * Mouth shape Open/Closed — switches the `_M-Open` / `_M-Closed` solo group.
 * Solos have no runtime API, so the toggle needs a ViewModel BOOLEAN with this
 * name bound to the solo's active item in Rive (true = _M-Open). The toggle in
 * the preview stays disabled until the property exists in the file.
 */
export const MOUTH_OPEN_BOOL = 'mouthOpen';

export type EditorSection = 'skin' | 'eyes' | 'hair';

/**
 * Known colour property names → which section they belong to + a display label.
 * These are the names to create on `ViewModel1` in Rive (type: color) and bind
 * to each vector shape's fill.
 */
export const COLOR_PROP_META: Record<string, { section: EditorSection; label: string }> = {
  skinBase: { section: 'skin', label: 'Skin base' },
  skinHighlight: { section: 'skin', label: 'Skin highlight' },
  blush: { section: 'skin', label: 'Blush' },
  skinLineShade: { section: 'skin', label: 'Skin line, shade' },
  eyeShade: { section: 'skin', label: 'Eye shade' },
  eyeLashes: { section: 'skin', label: 'Eye lashes' },
  mouthLine: { section: 'skin', label: 'Mouth line' },
  lips: { section: 'skin', label: 'Lips' },
  eyebrow: { section: 'hair', label: 'Eyebrow colour' },
};

/**
 * Known boolean property names. `isGirl` toggles the two mirrored eyelash
 * vectors (R-Eyelashes / L-Eyelashes) on/off. `frecklesOn` / `glassesOn`
 * (named so in fixes06) toggle those overlays. `top` booleans render in the
 * panel header. `mouthOpen` gets a dedicated toggle in the preview instead.
 */
export const BOOL_PROP_META: Record<
  string,
  { section: EditorSection | 'top'; label: string; onLabel?: string; offLabel?: string }
> = {
  isGirl: { section: 'top', label: 'Gender', onLabel: 'Girl', offLabel: 'Boy' },
  frecklesOn: { section: 'skin', label: 'Freckles' },
  glassesOn: { section: 'eyes', label: 'Eyeglasses' },
};

/** Known enum property names → section + label (e.g. hair style, eye shape). */
export const ENUM_PROP_META: Record<string, { section: EditorSection; label: string }> = {
  eyeShape: { section: 'eyes', label: 'Shape' },
  hairStyle: { section: 'hair', label: 'Style' },
};

/**
 * Eye & hair colour OPTIONS. These are not tints — the hex is only the swatch
 * icon/label. Each option attaches image(s) the user uploads, swapped into the
 * Rive asset slot(s) below via `setRenderImage` when the option is selected.
 * Hair options additionally carry an editable eyebrow colour (defaults to hex).
 */
export interface ColourOption {
  name: string;
  hex: string;
  /** Hair only: the default eyebrow colour for this option (editable in-editor). */
  eyebrow?: string;
}

export const EYE_COLOURS: ColourOption[] = [
  { name: 'Grey', hex: '#9AA0B5' },
  { name: 'Blue', hex: '#53789D' },
  { name: 'Green', hex: '#769F5D' },
  { name: 'Brown', hex: '#964829' },
  { name: 'Black', hex: '#2B0000' },
];

export const HAIR_COLOURS: ColourOption[] = [
  { name: 'Bright blonde', hex: '#FEF3D7', eyebrow: '#D9BB9A' },
  { name: 'Blonde', hex: '#FFE0A5', eyebrow: '#CFA369' },
  { name: 'Ginger', hex: '#F7B65B', eyebrow: '#C97C40' },
  { name: 'Red', hex: '#E46F45', eyebrow: '#AD5831' },
  { name: 'Dark Red', hex: '#8F3222', eyebrow: '#8F3D22' },
  { name: 'Dark Blonde', hex: '#B58666', eyebrow: '#8C4934' },
  { name: 'Brown', hex: '#5C3C2A', eyebrow: '#542C15' },
  { name: 'Black', hex: '#010101', eyebrow: '#1F150D' },
];

/**
 * Layered-hair recolour: each hairstyle is a stack of this many grayscale layer
 * PNGs (`<Style>_01.png` = bottom … `_0N.png` = top), recoloured in code. Each
 * hair colour carries this many editable shades (bottom→top / dark→light),
 * seeded via `seedShades()`. Discovery tolerates any layer count; this is only
 * the default shade-set length.
 */
export const HAIR_LAYER_COUNT = 5;

/** Rive asset slots the per-option uploaded images are swapped into. */
export const EYE_PUPIL_ASSET = 'Pupil-blue-ps';
/**
 * Folder-swappable overlay assets: drop image(s) into
 * `assets/freckles/` / `assets/glasses/` and the editor uses them (first file
 * alphabetically is the default; multiple files show a picker; the "—" tile
 * restores the .riv's own image). Freckles is EMBEDDED in the .riv — its
 * original bytes are stashed at load so the built-in tile can restore it.
 */
export const FRECKLES_ASSET = 'Freckles';
export const GLASSES_ASSET = 'Glasses-fix';
/**
 * DEFAULT slot names only. The .riv exposes two hair image slots (a front and a
 * back) whose names change with every designer export (the slot inherits the
 * name of the PNG last placed in it — e.g. "Pulled Back"/"Ponytail" in fixes15).
 * Because those names carry the HAIRSTYLE (not the layer position), the screen
 * detects the two hair slots by exclusion and assigns front/back by load order
 * (see `resolveHairSlots` in the screen); these are the fallbacks if detection
 * finds nothing. The recoloured layer stack is composited in code and pushed
 * into whichever slot is front / back.
 */
export const HAIR_LAYER_ASSETS = {
  front: '3-Hair Front-brown',
  back: '3-Hair Back-brown',
} as const;
export type HairLayer = keyof typeof HAIR_LAYER_ASSETS;
export const HAIR_LAYER_LABELS: Record<HairLayer, string> = {
  front: 'Front',
  back: 'Back',
};

/**
 * Referenced (out-of-band) image assets: the runtime calls the asset loader
 * with empty bytes for these, so we fetch the designer-exported PNG ourselves.
 * Maps the Rive asset NAME → public URL (filenames keep Rive's id suffix).
 */
export const ASSET_BASE = '/rive/sb_character_editor/assets';
export const REFERENCED_ASSET_URLS: Record<string, string> = {
  '3-Hair Back-brown': `${ASSET_BASE}/3-Hair Back-brown-6222198.png`,
  '3-Hair Front-brown': `${ASSET_BASE}/3-Hair Front-brown-6222212.png`,
  // Moved into the folder-driven pupils/ dir (2026-07-06), then under the
  // per-character `Child/` folder (2026-07-31): pupils are picked automatically
  // by eye-colour name (<Character>/pupils/<ColourName>.png); this entry is only
  // the base fallback when the selected colour has no file. These assets belong
  // to the head-editor file, i.e. the CHILD.
  'Pupil-blue-ps': `${ASSET_BASE}/Child/pupils/Blue.png`,
  // Moved into the folder-swappable glasses/ dir (2026-07-06), then under
  // `Child/` (2026-07-31). Only the fallback for an EMPTY folder — the folder's
  // first file wins.
  'Glasses-fix': `${ASSET_BASE}/Child/glasses/Glasses.png`,
};


export const SECTION_TITLES: Record<EditorSection, string> = {
  skin: 'Skin',
  eyes: 'Eyes',
  hair: 'Hair',
};
